import {
  BUSINESS_TIMEZONE,
  DEFAULT_USAGE_PAGE_SIZE,
  formatDateTime,
  formatDuration,
  formatUsdPrecise,
  USAGE_PAGE_SIZES,
  type UsageLog,
} from '@ytclip/shared'
import { BarChart3 } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { DateField } from '@/components/ui/field.tsx'
import { Badge, EmptyState, ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader, Pagination, TableScroller, Td, Th } from '@/components/ui/misc.tsx'
import { useUsageLogs } from '@/features/shared-hooks.ts'

/** Today in Jakarta, not in the browser's timezone — the server filters on WIB days. */
function jakartaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(new Date())
}

function jakartaDaysAgo(days: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TIMEZONE }).format(
    new Date(Date.now() - days * 86_400_000),
  )
}

export function UsagePage() {
  const [startDate, setStartDate] = useState(() => jakartaDaysAgo(6))
  const [endDate, setEndDate] = useState(jakartaToday)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_USAGE_PAGE_SIZE)

  const query = { startDate, endDate, page, pageSize }
  const { data, isPending, isError } = useUsageLogs(query)

  function applyRange(days: number) {
    setStartDate(jakartaDaysAgo(days - 1))
    setEndDate(jakartaToday())
    setPage(1)
  }

  return (
    <>
      <PageHeader title="Usage" description="Riwayat pemakaian dan biayanya" />

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <DateField
              label="Dari"
              value={startDate}
              max={endDate}
              onChange={(value) => {
                setStartDate(value)
                setPage(1)
              }}
            />
            <DateField
              label="Sampai"
              value={endDate}
              min={startDate}
              max={jakartaToday()}
              onChange={(value) => {
                setEndDate(value)
                setPage(1)
              }}
            />
            <div className="flex gap-2">
              <Button variant="outlined" size="sm" onClick={() => applyRange(7)}>
                7 hari
              </Button>
              <Button variant="outlined" size="sm" onClick={() => applyRange(30)}>
                30 hari
              </Button>
            </div>
          </div>
          <p className="text-label-sm text-on-surface-variant">
            Rentang tanggal mengikuti waktu Jakarta (WIB).
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {isPending ? (
            <LoadingBlock />
          ) : isError ? (
            <ErrorState message="Riwayat pemakaian tidak bisa dimuat." />
          ) : data.data.length === 0 ? (
            <EmptyState
              icon={<BarChart3 />}
              title="Belum ada pemakaian"
              description="Belum ada request di rentang tanggal ini."
            />
          ) : (
            <>
              <TableScroller>
                <table className="w-full min-w-3xl border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant">
                      <Th>Waktu</Th>
                      <Th>Model</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Token</Th>
                      <Th className="text-right">Durasi</Th>
                      <Th className="text-right">Biaya</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((log) => (
                      <UsageRow key={log.requestId} log={log} />
                    ))}
                  </tbody>
                </table>
              </TableScroller>

              <Pagination
                page={data.pagination.page}
                totalPages={data.pagination.totalPages}
                total={data.pagination.total}
                onPageChange={setPage}
                pageSize={pageSize}
                pageSizes={USAGE_PAGE_SIZES}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            </>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function UsageRow({ log }: { log: UsageLog }) {
  return (
    <tr className="border-b border-outline-variant/50 transition-colors hover:bg-surface-container-low">
      <Td className="whitespace-nowrap text-label-md">
        {log.startedAt ? formatDateTime(log.startedAt) : '—'}
      </Td>
      <Td className="font-medium">{log.model}</Td>
      <Td>
        <Badge tone={log.status === 'success' ? 'success' : 'error'}>
          {log.status === 'success' ? 'Sukses' : 'Gagal'}
        </Badge>
        {log.errorMessage ? (
          <span className="mt-1 block max-w-xs truncate text-label-sm text-on-surface-variant">
            {log.errorMessage}
          </span>
        ) : null}
      </Td>
      <Td className="text-right tabular-nums">{log.totalTokens.toLocaleString('id-ID')}</Td>
      <Td className="text-right tabular-nums">{formatDuration(log.durationMs)}</Td>
      <Td className="text-right font-medium tabular-nums">{formatUsdPrecise(log.spendUsd)}</Td>
    </tr>
  )
}
